import { useEffect, useState } from "react";
import axios from "axios";
import { MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const FALLBACK_URL = "https://chat.whatsapp.com/Bcx42eLHyjNAwAxUulAZbq?mode=gi_t";

/**
 * Hook: returns the WhatsApp group invite URL configured in admin settings.
 * Falls back to FALLBACK_URL if request fails or value missing.
 */
export function useWhatsAppGroupUrl() {
  const [url, setUrl] = useState(FALLBACK_URL);
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/settings/whatsapp-group`)
      .then(({ data }) => { if (!cancelled && data?.url) setUrl(data.url); })
      .catch(() => { /* keep fallback */ });
    return () => { cancelled = true; };
  }, []);
  return url;
}

/**
 * WhatsApp Group Button — replaces direct WhatsApp contact (Twilio-free).
 * Opens the public group invite link configured by admin.
 *
 * Variants:
 *  - "primary": large solid green CTA (use on success/contact pages)
 *  - "compact": smaller pill-shaped (use in cards/sidebars)
 *  - "footer": dark transparent (use inside dark footer)
 */
export function WhatsAppGroupButton({
  variant = "primary",
  showHours = true,
  label = "WhatsApp",
  className = "",
  testId = "whatsapp-group-btn",
}) {
  const url = useWhatsAppGroupUrl();

  const styles = {
    primary:
      "flex items-center justify-center gap-2 w-full py-3.5 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-xl text-sm transition-colors shadow-[0_4px_14px_rgba(37,211,102,0.25)] hover:shadow-[0_6px_20px_rgba(37,211,102,0.4)]",
    compact:
      "flex items-center justify-center gap-2 w-full py-3 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold rounded-xl text-sm transition-colors",
    footer:
      "inline-flex items-center gap-2 px-4 py-2.5 bg-[#25D366]/15 hover:bg-[#25D366]/25 border border-[#25D366]/30 text-[#25D366] font-medium rounded-lg text-sm transition-colors",
  };

  return (
    <div className={className}>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={styles[variant] || styles.primary}
        data-testid={testId}
      >
        <MessageCircle className="w-5 h-5" />
        {label}
      </a>
      {showHours && variant !== "footer" && (
        <p className="text-[10px] text-white/40 text-center mt-1.5">
          Hétfő-Péntek 8:00-18:00
        </p>
      )}
    </div>
  );
}
